import {
  LOCATION_TYPE_OPTIONS,
  ROAD_CONFIGURATION_OPTIONS,
  ROAD_SURFACE_OPTIONS,
  WEATHER_OPTIONS,
} from '@/config/surveyOptions';

/** Shape of the Survey Information form before it becomes a Survey record. */
export interface SurveyFormValues {
  surveyorName: string;
  surveyDate: string;
  plannedStartTime: string;
  latitude: string;
  longitude: string;
  gpsAccuracy: string;
  district: string;
  manualLocation: string;
  roadNumber: string;
  mccNumber: string;
  weather: string;
  locationType: string;
  roadSurface: string;
  roadConfiguration: string;
  direction: string;
  laneCount: number;
}

export type ValidationErrors = Partial<Record<keyof SurveyFormValues, string>>;

/**
 * Field order used to decide which invalid field to scroll to first. It matches
 * the visual order on the form.
 */
export const FIELD_ORDER: (keyof SurveyFormValues)[] = [
  'surveyorName',
  'surveyDate',
  'plannedStartTime',
  'latitude',
  'longitude',
  'district',
  'roadNumber',
  'mccNumber',
  'weather',
  'locationType',
  'roadSurface',
  'roadConfiguration',
  'direction',
  'laneCount',
];

const MAX_NAME = 120;
const MAX_TEXT = 200;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const HH_MM = /^([01]\d|2[0-3]):([0-5]\d)$/;

function isBlank(value: string): boolean {
  return value.trim().length === 0;
}

function optionValues(options: { value: string }[]): string[] {
  return options.map((o) => o.value);
}

/**
 * Validates the Survey Information form. Latitude/longitude are optional (GPS
 * may be denied) but must be valid numbers in range when provided.
 */
export function validateSurveyForm(values: SurveyFormValues): ValidationErrors {
  const errors: ValidationErrors = {};

  if (isBlank(values.surveyorName)) {
    errors.surveyorName = 'Enter the surveyor name.';
  } else if (values.surveyorName.trim().length > MAX_NAME) {
    errors.surveyorName = `Name must be ${MAX_NAME} characters or fewer.`;
  }

  if (isBlank(values.surveyDate)) {
    errors.surveyDate = 'Choose the survey date.';
  } else if (!ISO_DATE.test(values.surveyDate)) {
    errors.surveyDate = 'Date must look like 2026-09-18.';
  } else if (Number.isNaN(new Date(`${values.surveyDate}T00:00:00`).getTime())) {
    errors.surveyDate = 'That date does not exist.';
  }

  if (isBlank(values.plannedStartTime)) {
    errors.plannedStartTime = 'Choose the start time.';
  } else if (!HH_MM.test(values.plannedStartTime)) {
    errors.plannedStartTime = 'Time must look like 07:30.';
  }

  if (!isBlank(values.latitude)) {
    const lat = Number(values.latitude);
    if (!Number.isFinite(lat) || lat < -90 || lat > 90) {
      errors.latitude = 'Latitude must be between -90 and 90.';
    }
  }

  if (!isBlank(values.longitude)) {
    const lng = Number(values.longitude);
    if (!Number.isFinite(lng) || lng < -180 || lng > 180) {
      errors.longitude = 'Longitude must be between -180 and 180.';
    }
  }

  if (!isBlank(values.gpsAccuracy)) {
    const accuracy = Number(values.gpsAccuracy);
    if (!Number.isFinite(accuracy) || accuracy < 0) {
      errors.gpsAccuracy = 'Accuracy must be a positive number of metres.';
    }
  }

  // One coordinate without the other is almost always a typo.
  if (isBlank(values.latitude) !== isBlank(values.longitude)) {
    const message = 'Enter both latitude and longitude, or leave both empty.';
    if (isBlank(values.latitude)) errors.latitude = message;
    else errors.longitude = message;
  }

  if (isBlank(values.district)) {
    errors.district = 'Select the district (or type it manually).';
  }

  if (isBlank(values.roadNumber)) {
    errors.roadNumber = 'Enter the road number.';
  } else if (values.roadNumber.trim().length > MAX_TEXT) {
    errors.roadNumber = 'Road number is too long.';
  }

  if (isBlank(values.mccNumber)) {
    errors.mccNumber = 'Enter the MCC number.';
  } else if (values.mccNumber.trim().length > MAX_TEXT) {
    errors.mccNumber = 'MCC number is too long.';
  }

  if (!optionValues(WEATHER_OPTIONS).includes(values.weather)) {
    errors.weather = 'Select the weather at the start of the survey.';
  }
  if (!optionValues(LOCATION_TYPE_OPTIONS).includes(values.locationType)) {
    errors.locationType = 'Select the location type.';
  }
  if (!optionValues(ROAD_SURFACE_OPTIONS).includes(values.roadSurface)) {
    errors.roadSurface = 'Select the road surface.';
  }
  if (!optionValues(ROAD_CONFIGURATION_OPTIONS).includes(values.roadConfiguration)) {
    errors.roadConfiguration = 'Select the road configuration.';
  }

  if (isBlank(values.direction)) {
    errors.direction = 'Enter the direction, e.g. Colombo to Kandy.';
  } else if (values.direction.trim().length > MAX_TEXT) {
    errors.direction = 'Direction is too long.';
  }

  if (!Number.isInteger(values.laneCount) || values.laneCount < 1) {
    errors.laneCount = 'There must be at least 1 lane.';
  } else if (values.laneCount > 20) {
    errors.laneCount = 'That is more lanes than any road here has.';
  }

  return errors;
}

export function isValid(errors: ValidationErrors): boolean {
  return Object.keys(errors).length === 0;
}

/** First invalid field in visual order, so the form can scroll to it. */
export function firstInvalidField(errors: ValidationErrors): keyof SurveyFormValues | null {
  for (const field of FIELD_ORDER) {
    if (errors[field]) return field;
  }
  const remaining = Object.keys(errors) as (keyof SurveyFormValues)[];
  return remaining.length > 0 ? remaining[0] : null;
}
